// PagerDuty incident-alert page mockup, styled to match a real PagerDuty
// incident detail view: dark near-black nav chrome, PagerDuty green accent,
// a red TRIGGERED status banner, Responders section, and a vertical
// activity timeline. Shown after "Simulate Outage" alongside the other
// demo-fanout mockups (ServiceNow ticket, etc). Fully local/decorative —
// no backend call, no real PagerDuty alert is created. Acknowledge and
// Resolve are both decorative and just call onClose (documented inline).
import '../styles/pagerduty-alert-mockup.css';

export interface PagerDutyIncident {
  id: string;
  severity: string;
  current_status: string;
  impact_statement: string | null;
  service_name: string | null;
  affected_site: string | null;
  estimated_users_impacted: number | null;
  workaround: string | null;
  is_demo: boolean;
  state: string;
  declared_at: string | null;
}

function IcoBell() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M8 1.7c-2 0-3.4 1.6-3.4 3.6v2.1c0 .5-.2 1-.6 1.4L3 9.8v.7h10v-.7l-1-1c-.4-.4-.6-.9-.6-1.4V5.3c0-2-1.4-3.6-3.4-3.6z" strokeLinejoin="round" />
      <path d="M6.3 12.3a1.7 1.7 0 0 0 3.4 0" strokeLinecap="round" />
    </svg>
  );
}
function IcoChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3.5 6 8 10.5 12.5 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoClose() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" strokeLinecap="round" />
    </svg>
  );
}
function IcoBolt() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8.6 1.3 3 9.2h3.6l-.9 5.5L12.5 6.6H8.9z" />
    </svg>
  );
}
function IcoCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 8.5l3.2 3.2L13 4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Fabricated on-call responder — this is a demo mockup, not a real page.
const ON_CALL_NAME = 'J. Alvarez';
const ON_CALL_TITLE = 'On-Call SRE';
const ON_CALL_INITIALS = 'JA';
const ESCALATION_POLICY = 'Major Incident — Default';

function priorityFor(severity: string): string {
  return severity === 'critical' ? 'P1' : severity === 'high' ? 'P2' : 'P3';
}

function incidentNumber(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return String((h % 90000) + 10000);
}

function formatClock(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function addSeconds(iso: string | null, secs: number): string | null {
  const base = iso ? new Date(iso) : new Date();
  if (isNaN(base.getTime())) return null;
  return new Date(base.getTime() + secs * 1000).toISOString();
}

export default function PagerDutyAlertMockup({ incident, onClose }: { incident: PagerDutyIncident; onClose: () => void }) {
  const number = incidentNumber(incident.id + (incident.declared_at || ''));
  const title = incident.impact_statement || incident.service_name || 'Major service disruption detected';
  const service = incident.service_name || 'Unnamed Service';

  // Relative activity timeline, seeded off declared_at when available so the
  // entries read as a plausible real sequence rather than all-identical stamps.
  const tTriggered = incident.declared_at;
  const tNotified = addSeconds(incident.declared_at, 8);
  const tAck = addSeconds(incident.declared_at, 41);
  const tEscalate = addSeconds(incident.declared_at, 300);

  return (
    <div className="pd-mock-overlay" onClick={onClose}>
      <div className="pd-mock-card" onClick={(e) => e.stopPropagation()}>
        <div className="pd-mock-nav">
          <div className="pd-mock-nav-left">
            <span className="pd-mock-logo">
              <IcoBolt /> PagerDuty
            </span>
            <span className="pd-mock-nav-crumb">Incidents<span className="pd-mock-nav-sep">/</span>#{number}</span>
          </div>
          <button className="pd-mock-close" onClick={onClose} aria-label="Close" title="Close">
            <IcoClose />
          </button>
        </div>

        <div className="pd-mock-banner">
          <span className="pd-mock-banner-pill">TRIGGERED</span>
          <span className="pd-mock-banner-text">This incident is unacknowledged</span>
        </div>

        <div className="pd-mock-body">
          <div className="pd-mock-titlerow">
            <div>
              <div className="pd-mock-idline">#{number} &middot; {service}</div>
              <h2 className="pd-mock-title">{title}</h2>
            </div>
            <div className="pd-mock-actions">
              <button className="pd-mock-btn pd-mock-btn--ack" onClick={onClose}>Acknowledge</button>
              <button className="pd-mock-btn pd-mock-btn--resolve" onClick={onClose}>Resolve</button>
            </div>
          </div>

          <div className="pd-mock-meta">
            <div className="pd-mock-meta-item">
              <span className="pd-mock-meta-label">Urgency</span>
              <span className="pd-mock-meta-value pd-mock-urgency"><IcoBell /> High</span>
            </div>
            <div className="pd-mock-meta-item">
              <span className="pd-mock-meta-label">Priority</span>
              <span className="pd-mock-meta-value">{priorityFor(incident.severity)}</span>
            </div>
            <div className="pd-mock-meta-item">
              <span className="pd-mock-meta-label">Status</span>
              <span className="pd-mock-meta-value">{incident.current_status || 'triggered'}</span>
            </div>
            <div className="pd-mock-meta-item">
              <span className="pd-mock-meta-label">Service</span>
              <span className="pd-mock-meta-value">{service}</span>
            </div>
            <div className="pd-mock-meta-item">
              <span className="pd-mock-meta-label">Escalation Policy</span>
              <span className="pd-mock-meta-value">{ESCALATION_POLICY}</span>
            </div>
            {incident.affected_site && (
              <div className="pd-mock-meta-item">
                <span className="pd-mock-meta-label">Site</span>
                <span className="pd-mock-meta-value">{incident.affected_site}</span>
              </div>
            )}
            {incident.estimated_users_impacted != null && (
              <div className="pd-mock-meta-item">
                <span className="pd-mock-meta-label">Users impacted</span>
                <span className="pd-mock-meta-value">{incident.estimated_users_impacted.toLocaleString()}</span>
              </div>
            )}
          </div>

          {incident.workaround && (
            <div className="pd-mock-workaround">
              <span className="pd-mock-workaround-label">Workaround</span>
              <span className="pd-mock-workaround-text">{incident.workaround}</span>
            </div>
          )}

          <div className="pd-mock-section">
            <div className="pd-mock-section-head">
              Responders
              <IcoChevronDown />
            </div>
            <div className="pd-mock-responder">
              <span className="pd-mock-avatar">{ON_CALL_INITIALS}</span>
              <div className="pd-mock-responder-info">
                <span className="pd-mock-responder-name">{ON_CALL_NAME}</span>
                <span className="pd-mock-responder-role">{ON_CALL_TITLE}</span>
              </div>
              <span className="pd-mock-responder-status">Notified</span>
            </div>
          </div>

          <div className="pd-mock-section">
            <div className="pd-mock-section-head">
              Activity
              <IcoChevronDown />
            </div>
            <ul className="pd-mock-timeline">
              <li className="pd-mock-timeline-item">
                <span className="pd-mock-timeline-dot pd-mock-timeline-dot--red" />
                <div className="pd-mock-timeline-content">
                  <span className="pd-mock-timeline-text">Triggered via Events API</span>
                  <span className="pd-mock-timeline-time">{formatClock(tTriggered)}</span>
                </div>
              </li>
              <li className="pd-mock-timeline-item">
                <span className="pd-mock-timeline-dot" />
                <div className="pd-mock-timeline-content">
                  <span className="pd-mock-timeline-text">Notified {ON_CALL_NAME}</span>
                  <span className="pd-mock-timeline-time">{formatClock(tNotified)}</span>
                </div>
              </li>
              <li className="pd-mock-timeline-item">
                <span className="pd-mock-timeline-dot pd-mock-timeline-dot--pending" />
                <div className="pd-mock-timeline-content">
                  <span className="pd-mock-timeline-text pd-mock-timeline-text--pending">Awaiting acknowledgement</span>
                  <span className="pd-mock-timeline-time">{formatClock(tAck)}</span>
                </div>
              </li>
              <li className="pd-mock-timeline-item">
                <span className="pd-mock-timeline-dot pd-mock-timeline-dot--pending" />
                <div className="pd-mock-timeline-content">
                  <span className="pd-mock-timeline-text pd-mock-timeline-text--pending">
                    Escalating to Level 2 ({ESCALATION_POLICY}) if still unacknowledged
                  </span>
                  <span className="pd-mock-timeline-time">{formatClock(tEscalate)}</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="pd-mock-section">
            <div className="pd-mock-section-head">
              Details
              <IcoChevronDown />
            </div>
            <div className="pd-mock-meta">
              <div className="pd-mock-meta-item">
                <span className="pd-mock-meta-label">Incident Key</span>
                <span className="pd-mock-meta-value pd-mock-mono">{incident.id}</span>
              </div>
              <div className="pd-mock-meta-item">
                <span className="pd-mock-meta-label">Integration</span>
                <span className="pd-mock-meta-value">Events API v2</span>
              </div>
            </div>
          </div>

          <div className="pd-mock-foot">
            <IcoCheck />
            <span>Demo mockup &mdash; Acknowledge / Resolve do not page anyone, they just close this view.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
